import { gql } from '@apollo/client';
import { AdminLayout } from 'components/admin/admin-layout';
import { TextEditor } from 'components/text-editor/text-editor';
import { useInput } from 'lib/hooks/use-input';
import {
  Category_Book_Constraint,
  useInsertNewBookMutation,
} from 'lib/graphql-generated';
import { Button } from 'components/buttons/button';
import { useState } from 'react';
import { EditorState } from 'draft-js';
import currency from 'currency.js';
import { TagSelectorValue, TagsSelector } from 'components/forms/tag-selector';
import {
  CategoriesSelector,
  CategorySelectorValue,
} from 'components/forms/category-selector';
import { useRouter } from 'next/router';
import { labelClassNames, StandardInput } from 'components/forms/input';
import { saveDraftContent } from 'components/text-editor/editor-utils';
import Link from 'next/link';

const INSERT_BOOK = gql`
  mutation InsertNewBook($data: books_insert_input!) {
    insert_books_one(object: $data) {
      isbn
    }
  }
`;

export const NewBook = () => {
  const router = useRouter();
  const title = useInput<string>('');
  const description = useInput<EditorState>(null);
  const price = useInput<number>(0);
  const stock = useInput<number>(0);
  const attributes = useInput<string>('');
  const isbn = useInput<string>('');
  const author = useInput<string>('');
  const image = useInput<string>('');
  const tags = useInput<TagSelectorValue[]>([]);
  const discount = useInput<number>(0);
  const categories = useInput<CategorySelectorValue[]>([]);
  const isUnlisted = useInput<boolean>(false);
  const [shouldTrackStock, setTrackStock] = useState<boolean>(false);
  const [hasDiscount, setHasDiscount] = useState<boolean>(false);
  const [save, { error, data, loading }] = useInsertNewBookMutation();
  const isSaveDisabled =
    !(
      title.isModified &&
      author.isModified &&
      price.isModified &&
      isbn.isModified &&
      image.isModified
    ) || loading;

  const handleSave = (e) => {
    e.preventDefault();
    save({
      variables: {
        data: {
          isbn: isbn.value,
          title: title.value,
          author: author.value,
          price: currency(price.value).intValue,
          is_discounted: hasDiscount,
          discount_value: currency(discount.value).intValue,
          stock: stock.value,
          track_stock: shouldTrackStock,
          attributes: attributes.value,
          image: image.value,
          is_unlisted: isUnlisted.value,
          category_books: {
            data: categories.value.map((c) => ({ category_name: c.data.name })),
            on_conflict: {
              constraint: Category_Book_Constraint.CategoryBookPkey,
              update_columns: [],
            },
          },
          description: saveDraftContent(description.value),
          tag_books: {
            data: tags.value.map((tag) => ({
              tag_id: tag.data.id,
            })),
          },
        },
      },
    }).then(({ data }) => {
      if (data) router.push(`/admin/books/${data.insert_books_one.isbn}`);
    });
  };

  return (
    <>
      <AdminLayout page='New Book'>
        <article className='space-y-4'>
          <header className='flex flex-row justify-between'>
            <h1 className='header text-4xl'>Creating New Book</h1>
            <section className='space-x-4'>
              <Button
                buttonType='primary'
                buttonSize='md'
                disabled={isSaveDisabled}
                onClick={handleSave}>
                Save
              </Button>
              <Button buttonType='ghost' buttonSize='md' href='/admin/books'>
                Cancel
              </Button>
            </section>
          </header>
          {error &&
            error.graphQLErrors.some(
              (err) => err.extensions.code === 'constraint-violation'
            ) && (
              <section className='bg-red-50 text-red-700 rounded-md p-4 text-center'>
                This Book already exists in your database.{' '}
                <Link href={`/admin/books/${isbn.value}`}>
                  <a className='underline text-red-900 hover:text-red-700'>
                    Click here to view it
                  </a>
                </Link>
              </section>
            )}
          <form
            className='flex flex-row flex-wrap w-full space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-4 xl:gap-8'
            onSubmit={handleSave}>
            <StandardInput
              label='Title'
              name='title'
              id='title'
              type='text'
              bind={{ ...title.bind }}
            />{' '}
            <StandardInput
              label='Authr'
              name='author'
              id='author'
              type='text'
              bind={{ ...author.bind }}
            />{' '}
            <StandardInput
              label='ISBN-13'
              name='text'
              id='isbn'
              type='isbn'
              bind={{ ...isbn.bind }}
            />{' '}
            <StandardInput
              label='Price'
              name='price'
              id='price'
              type='number'
              bind={{ ...price.bind }}
            />
            <StandardInput
              label='Attributes'
              name='attributes'
              id='attributes'
              type='text'
              bind={{ ...attributes.bind }}
            />
            <StandardInput
              label='Image'
              name='image'
              id='image'
              type='text'
              bind={{ ...image.bind }}
            />
            <label
              htmlFor='isUnlisted'
              className={`${labelClassNames} flex flex-col`}>
              Unlist
              <input
                type='checkbox'
                name='isUnlisted'
                id='isUnlisted'
                className='mr-2 h-8 w-8 mt-1 rounded text-green-default'
                checked={isUnlisted.value}
                onChange={(e) => {
                  isUnlisted.setValue(e.target.checked);
                }}
              />
            </label>
            <TagsSelector
              label='Tags'
              value={tags.value}
              setValue={tags.setValue}
            />
            <CategoriesSelector
              label='Categories'
              value={categories.value}
              setValue={categories.setValue}
            />
            <section>
              <label htmlFor='stock' className='w-full flex flex-col'>
                Stock
                <input
                  type='number'
                  name='stock'
                  id='stock'
                  {...stock.bind}
                  disabled={!shouldTrackStock}
                />
              </label>

              <label htmlFor='useStock'>
                <input
                  type='checkbox'
                  name='useStock'
                  id='useStock'
                  className='mr-2 '
                  checked={shouldTrackStock}
                  onChange={(e) => {
                    setTrackStock(e.target.checked);
                  }}
                />
                Track Stock
              </label>
            </section>
            <section>
              <label htmlFor='discount' className='w-full flex flex-col'>
                Discount
                <input
                  type='number'
                  name='discount'
                  id='discount'
                  {...discount.bind}
                  disabled={!hasDiscount}
                />
              </label>

              <label htmlFor='useDiscount'>
                <input
                  type='checkbox'
                  name='useDiscount'
                  id='useDiscount'
                  className='mr-2 '
                  checked={hasDiscount}
                  onChange={(e) => {
                    setHasDiscount(e.target.checked);
                  }}
                />
                Apply Discount
              </label>
            </section>
            <label htmlFor='description' className='w-full col-span-full'>
              Description{' '}
              <TextEditor
                state={description.value}
                setState={description.setValue}
              />
            </label>
          </form>
        </article>
      </AdminLayout>
    </>
  );
};

export default NewBook;
